/**
 * Audit Log Item Component
 *
 * Single row display for an audit log entry.
 * @see US-19a.28
 */

import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  LogIn,
  LogOut,
  Bot,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { Badge, Tooltip } from '@/components/ui';
import type { AuditLogListItem, AuditAction, ActorType } from '@bstock/shared';
import {
  formatAuditMessage,
  formatRelativeTime,
  formatAbsoluteDateTime,
} from '../utils/format-audit-message';
import { cn } from '@/lib/utils';

interface AuditLogItemProps {
  log: AuditLogListItem;
  onClick?: () => void;
}

/**
 * Get icon and color for each action type
 */
function getActionStyle(action: AuditAction): {
  icon: React.ReactNode;
  bgColor: string;
  iconColor: string;
} {
  switch (action) {
    case 'CREATE':
      return {
        icon: <Plus className="w-4 h-4" />,
        bgColor: 'bg-success-100',
        iconColor: 'text-success-600',
      };
    case 'UPDATE':
      return {
        icon: <Pencil className="w-4 h-4" />,
        bgColor: 'bg-blue-100',
        iconColor: 'text-blue-600',
      };
    case 'DELETE':
      return {
        icon: <Trash2 className="w-4 h-4" />,
        bgColor: 'bg-danger-100',
        iconColor: 'text-danger-600',
      };
    case 'VALIDATE':
      return {
        icon: <CheckCircle className="w-4 h-4" />,
        bgColor: 'bg-success-100',
        iconColor: 'text-success-600',
      };
    case 'CANCEL':
      return {
        icon: <XCircle className="w-4 h-4" />,
        bgColor: 'bg-warning-100',
        iconColor: 'text-warning-600',
      };
    case 'LOGIN':
      return {
        icon: <LogIn className="w-4 h-4" />,
        bgColor: 'bg-primary-100',
        iconColor: 'text-primary-600',
      };
    case 'LOGOUT':
      return {
        icon: <LogOut className="w-4 h-4" />,
        bgColor: 'bg-neutral-100',
        iconColor: 'text-neutral-600',
      };
    default:
      return {
        icon: <Pencil className="w-4 h-4" />,
        bgColor: 'bg-neutral-100',
        iconColor: 'text-neutral-600',
      };
  }
}

/**
 * Get badge variant for actor type
 */
function getActorTypeBadge(actorType: ActorType): {
  label: string;
  variant: 'primary' | 'info' | 'warning' | 'neutral';
} | null {
  switch (actorType) {
    case 'SYSTEM':
      return { label: 'Systeme', variant: 'info' };
    case 'CRON':
      return { label: 'Planifie', variant: 'warning' };
    case 'API':
      return { label: 'API', variant: 'neutral' };
    default:
      return null;
  }
}

export function AuditLogItem({ log, onClick }: AuditLogItemProps) {
  const { icon, bgColor, iconColor } = getActionStyle(log.action);
  const actorBadge = getActorTypeBadge(log.actorType);
  const message = formatAuditMessage(log);
  const relativeTime = formatRelativeTime(log.occurredAt);
  const absoluteTime = formatAbsoluteDateTime(log.occurredAt);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-4 p-4 text-left transition-colors',
        'hover:bg-neutral-50 focus:outline-none focus:bg-neutral-50',
        'border-b border-neutral-100 last:border-0'
      )}
    >
      {/* Action icon */}
      <div className={cn('p-2 rounded-lg flex-shrink-0', bgColor)}>
        <span className={iconColor}>{icon}</span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Message */}
        <p className="text-sm text-dark-500 font-medium">
          {message}
        </p>

        {/* Meta info */}
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {/* Actor type badge (for non-user actors) */}
          {actorBadge && (
            <Badge variant={actorBadge.variant} className="text-xs">
              <Bot className="w-3 h-3 mr-1" />
              {actorBadge.label}
            </Badge>
          )}

          {/* Time */}
          <Tooltip content={absoluteTime}>
            <span className="flex items-center gap-1 text-xs text-neutral-500">
              <Clock className="w-3 h-3" />
              {relativeTime}
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Chevron indicator */}
      <div className="flex-shrink-0 text-neutral-400">
        <ChevronRight className="w-5 h-5" />
      </div>
    </button>
  );
}
